import React, { useState } from 'react';
import ModalCloseButton from './ui/ModalCloseButton';
import { trackEvent } from '../utils/telemetry';

export default function BulkCartExportModal({ isOpen, onClose, items = [], theme = {}, eventData = {} }) {
  const [copied, setCopied] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');

  if (!isOpen) return null;

  const country = eventData?.country || 'US';
  const amazonDomain = {
    'US': 'amazon.com',
    'UK': 'amazon.co.uk',
    'CA': 'amazon.ca',
    'AU': 'amazon.com.au',
  }[country] || 'amazon.com';

  const categories = ['all', ...Array.from(new Set(items.map(i => i.category || 'Decor').filter(Boolean)))];

  const filteredItems = selectedCategory === 'all'
    ? items
    : items.filter(i => (i.category || 'Decor') === selectedCategory);

  const themeName = theme?.name || eventData?.theme || 'Party';
  const affiliateTag = process.env.REACT_APP_AMAZON_AFFILIATE_TAG || 'craftmypart00-20';
  const amazonTagParam = affiliateTag ? `&tag=${encodeURIComponent(affiliateTag)}` : '';

  // Build a concise search query bundle
  const topKeywords = items
    .slice(0, 4)
    .map(i => i.name)
    .filter(Boolean)
    .join(' ');
  const bundleSearchUrl = `https://www.${amazonDomain}/s?k=${encodeURIComponent(`${themeName} party supplies ${topKeywords}`.slice(0, 100))}${amazonTagParam}`;
  const targetSearchUrl = `https://www.target.com/s?searchTerm=${encodeURIComponent(`${themeName} party supplies`)}`;
  const walmartSearchUrl = `https://www.walmart.com/search?q=${encodeURIComponent(`${themeName} party supplies`)}`;

  const handleCopyText = () => {
    let text = `🎉 CRAFTMYPARTY CHECKLIST: ${themeName.toUpperCase()}\n`;
    if (eventData?.occasion) text += `Occasion: ${eventData.occasion} | Guests: ${eventData.guest_count || 'N/A'}\n`;
    text += `Generated on CraftMyParty\n\n`;

    const grouped = {};
    items.forEach(item => {
      const cat = item.category || 'Decor & Essentials';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(item);
    });

    Object.entries(grouped).forEach(([cat, catItems]) => {
      text += `--- ${cat.toUpperCase()} ---\n`;
      catItems.forEach(i => {
        const qty = i.quantity ? ` (${i.quantity})` : '';
        const price = (i.buyPrice || i.buy_price || i.optimizedPrice) ? ` - $${i.buyPrice || i.buy_price || i.optimizedPrice}` : '';
        text += `[ ] ${i.name}${qty}${price}\n`;
      });
      text += `\n`;
    });

    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      trackEvent('cart_export_copy_checklist', { itemCount: items.length, theme: themeName });
      setTimeout(() => setCopied(false), 2500);
    }).catch(() => {});
  };

  const handlePrint = () => {
    trackEvent('cart_export_print', { itemCount: items.length });
    window.print();
  };

  return (
    <div className="fixed inset-0 z-70 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div 
        className="relative w-full max-w-2xl max-h-[90vh] bg-white rounded-2xl shadow-2xl border border-stone-200 flex flex-col overflow-hidden"
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-stone-100 bg-gradient-to-r from-stone-50 via-white to-stone-50 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-primary/10 text-primary text-xl">
              🛒
            </span>
            <div>
              <h2 className="text-base sm:text-lg font-black text-stone-900 leading-tight">
                1-Click Cart & Shopping Exporter
              </h2>
              <p className="text-xs text-stone-500">
                {items.length} curated items for <span className="font-semibold text-stone-800">{themeName}</span>
              </p>
            </div>
          </div>
          <ModalCloseButton onClose={onClose} />
        </div>

        {/* Action Toolbar */}
        <div className="p-4 sm:p-5 bg-stone-50/70 border-b border-stone-100 space-y-3">
          <p className="text-[11px] font-bold uppercase tracking-wider text-stone-500">
            Fast Sourcing & Retailer Bundles
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <a
              href={bundleSearchUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('cart_export_retailer_clicked', { retailer: 'Amazon' })}
              className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-stone-950 font-bold text-xs transition shadow-sm active:scale-[0.98]"
            >
              <span>📦</span>
              <span>Amazon Search</span>
            </a>
            <a
              href={targetSearchUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('cart_export_retailer_clicked', { retailer: 'Target' })}
              className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-xs transition shadow-sm active:scale-[0.98]"
            >
              <span>🎯</span>
              <span>Target Search</span>
            </a>
            <a
              href={walmartSearchUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('cart_export_retailer_clicked', { retailer: 'Walmart' })}
              className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs transition shadow-sm active:scale-[0.98]"
            >
              <span>🏬</span>
              <span>Walmart Search</span>
            </a>
            <button
              type="button"
              onClick={handleCopyText}
              className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl font-bold text-xs transition shadow-sm active:scale-[0.98] ${
                copied
                  ? 'bg-emerald-600 text-white'
                  : 'bg-stone-900 hover:bg-stone-800 text-white'
              }`}
            >
              <span>{copied ? '✓' : '📋'}</span>
              <span>{copied ? 'Copied!' : 'Copy Checklist'}</span>
            </button>
          </div>

          {/* Print & Filter sub-row */}
          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <div className="flex items-center gap-1 overflow-x-auto pb-1 max-w-full">
              {categories.slice(0, 5).map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-2.5 py-1 text-[11px] font-semibold rounded-lg capitalize whitespace-nowrap transition ${
                    selectedCategory === cat
                      ? 'bg-stone-900 text-white'
                      : 'bg-white border border-stone-200 text-stone-600 hover:bg-stone-100'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={handlePrint}
              className="text-xs font-bold text-stone-600 hover:text-stone-900 flex items-center gap-1 ml-auto"
            >
              <span>🖨️</span>
              <span>Print Checklist</span>
            </button>
          </div>
        </div>

        {/* Item List */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-5 divide-y divide-stone-100">
          {filteredItems.length === 0 ? (
            <div className="text-center py-8 text-stone-400 text-sm">
              No items in this category.
            </div>
          ) : (
            filteredItems.map((item, idx) => {
              const itemAmazonUrl = `https://www.${amazonDomain}/s?k=${encodeURIComponent(`${item.name} ${themeName}`)}${amazonTagParam}`;
              const price = item.buyPrice || item.buy_price || item.optimizedPrice;
              return (
                <div key={item.id || idx} className="py-2.5 flex items-center justify-between gap-3 text-left">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-stone-900 truncate">
                        {item.name}
                      </span>
                      {item.category && (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-stone-100 text-stone-600 shrink-0">
                          {item.category}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-stone-500 flex items-center gap-2 mt-0.5">
                      {item.quantity && <span>Qty: {item.quantity}</span>}
                      {price && <span className="font-semibold text-emerald-700">${price}</span>}
                      {item.purchaseType && (
                        <span className="capitalize text-[10px] text-stone-400">({item.purchaseType.replace('_', ' ')})</span>
                      )}
                    </div>
                  </div>
                  <a
                    href={itemAmazonUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackEvent('cart_export_single_item_clicked', { itemName: item.name })}
                    className="shrink-0 px-2.5 py-1 rounded-lg border border-stone-200 text-stone-700 hover:border-amber-500 hover:text-amber-700 hover:bg-amber-50 font-bold text-xs transition flex items-center gap-1"
                  >
                    <span>Amazon</span>
                    <span className="text-[10px]">↗</span>
                  </a>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-stone-100 bg-stone-50 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs text-stone-500">
          <div>
            <span>Tip: Tap <strong>Copy Checklist</strong> to paste into iMessage or Notes.</span>
            <p className="text-[10px] text-stone-400 mt-0.5">As an Amazon Associate, CraftMyParty earns from qualifying purchases.</p>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-1.5 rounded-lg bg-stone-200 hover:bg-stone-300 text-stone-800 font-bold transition self-end sm:self-auto shrink-0"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
